import type { ChatMessage as ChatMessageType } from "../../types/chat";
import { SourcesList } from "./SourcesList";

interface ChatMessageProps {
  message: ChatMessageType;
  userAvatar?: string;
  /** Persona avatar image path (e.g., "/images/akela.jpg") */
  personaAvatar?: string;
  /** When true, sources are collapsible (collapsed by default) */
  collapsibleSources?: boolean;
}

/**
 * Avatar component for the AI persona
 */
function PersonaAvatarDisplay({ avatar }: { avatar?: string }) {
  if (avatar) {
    return (
      <img
        src={avatar}
        alt="AI"
        className="w-12 h-12 rounded-full flex-shrink-0 object-cover border-2 border-primary-container"
      />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full flex-shrink-0 bg-primary-container flex items-center justify-center text-3xl">
      🐺
    </div>
  );
}

/**
 * Renders a single chat message (user or assistant)
 */
export function ChatMessage({
  message,
  userAvatar,
  personaAvatar,
  collapsibleSources = false,
}: ChatMessageProps) {
  const isUser = message.role === "user";
  // Use displayContent for UI if available, otherwise use full content
  const displayText = message.displayContent ?? message.content;

  if (isUser) {
    return (
      <div className="flex gap-3 items-start justify-end">
        <div className="flex-1 flex justify-end">
          <div className="bg-primary text-on-primary rounded-2xl rounded-tr-none shadow-1 p-4 max-w-2xl">
            <p className="whitespace-pre-wrap">{displayText}</p>
          </div>
        </div>
        <img
          src={userAvatar ?? "/images/user-default.svg"}
          className="w-12 h-12 rounded-full flex-shrink-0"
          alt="You"
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex gap-3 items-start">
      <PersonaAvatarDisplay avatar={personaAvatar} />
      <div className="flex-1">
        <div className="bg-surface-container-lowest rounded-2xl rounded-tl-none shadow-1 p-4 max-w-2xl">
          <p className="text-on-surface whitespace-pre-wrap">{message.content}</p>

          {/* Enriched sources display */}
          {message.sources && message.sources.length > 0 && (
            <SourcesList
              sources={message.sources}
              collapsible={collapsibleSources}
              defaultCollapsed={collapsibleSources}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Error message component
 */
export function ErrorMessage({
  message,
  personaAvatar,
}: {
  message: string;
  personaAvatar?: string;
}) {
  return (
    <div className="flex gap-3 items-start">
      <PersonaAvatarDisplay avatar={personaAvatar} />
      <div className="flex-1">
        <div className="bg-error-container border border-error/20 rounded-2xl rounded-tl-none shadow-1 p-4 max-w-2xl">
          <p className="text-on-error-container">❌ {message}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Typing indicator for when AI is thinking
 */
export function TypingIndicator({ personaAvatar }: { personaAvatar?: string }) {
  return (
    <div className="flex gap-3 items-start">
      <PersonaAvatarDisplay avatar={personaAvatar} />
      <div className="flex-1">
        <div className="bg-surface-container-lowest rounded-2xl rounded-tl-none shadow-1 p-4 max-w-2xl">
          <div className="flex gap-1">
            <div
              className="w-2 h-2 bg-outline rounded-full animate-bounce"
              style={{ animationDelay: "0ms" }}
            ></div>
            <div
              className="w-2 h-2 bg-outline rounded-full animate-bounce"
              style={{ animationDelay: "150ms" }}
            ></div>
            <div
              className="w-2 h-2 bg-outline rounded-full animate-bounce"
              style={{ animationDelay: "300ms" }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Rate limit countdown indicator
 */
export function RateLimitIndicator({
  seconds,
  personaAvatar,
}: {
  seconds: number;
  personaAvatar?: string;
}) {
  return (
    <div className="flex gap-3 items-start">
      <PersonaAvatarDisplay avatar={personaAvatar} />
      <div className="flex-1">
        <div className="bg-tertiary-container border border-tertiary/20 rounded-2xl rounded-tl-none shadow-1 p-4 max-w-2xl">
          <p className="text-on-tertiary-container">
            ⏳ Even geduld. Ik probeer het over <strong>{seconds}</strong> seconden opnieuw...
          </p>
        </div>
      </div>
    </div>
  );
}
